/* wg-blog-carousel.js - Wade Greens
 *
 * Drives sections/blog-carousel.liquid. Every instance on the page is wired
 * independently and reads its options off the section element's data-sc-*
 * attributes, so nothing here is section-id specific and one copy serves them
 * all -- which is also why it lives in an asset: blog-carousel.liquid was
 * 53KB with this inline, and the Admin API cannot write a file that big.
 *
 * Each TAB gets its own pane, and each pane is a complete carousel: its own
 * track, arrows, progress bar and drag handling. Behaviour is carried over
 * from sections/elevated-carousel.liquid unchanged -- the only difference is
 * that it is wired per pane instead of once per section.
 *
 * A HIDDEN PANE HAS NO MEASURABLE WIDTH. scrollWidth and clientWidth both read
 * 0 on display:none, so arrows and the progress bar cannot be sized until the
 * pane is on screen. That is why selectTab() calls refresh() after unhiding
 * rather than at init.
 */
(function () {
  'use strict';

  function boot(root) {
    if (root.__scBooted) return;
    root.__scBooted = true;

    var LOOP = root.getAttribute('data-sc-loop') === 'true';
    var AUTOPLAY = root.getAttribute('data-sc-autoplay') === 'true';
    var AUTOPLAY_MS = parseInt(root.getAttribute('data-sc-autoplay-ms'), 10) || 5000;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Each pane is an independent carousel. Identical behaviour to the Shops
       Carousel - the only change is that it is wired per pane instead of once. */
    function initPane(pane) {
      var track = pane.querySelector('[data-sc-track]');
      if (!track || track.__scInit) return null;
      track.__scInit = true;

      var prevBtn = pane.querySelector('[data-sc-prev]');
      var nextBtn = pane.querySelector('[data-sc-next]');
      var progFill = pane.querySelector('[data-sc-progress-fill]');
      var progTrack = pane.querySelector('[data-sc-progress]');

      function cardStep() {
        var card = track.querySelector('.sc__card');
        if (!card) return track.clientWidth * 0.8;
        var cs = getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
        return card.getBoundingClientRect().width + gap;
      }
      function atEnd() { return track.scrollLeft + track.clientWidth >= track.scrollWidth - 4; }
      function atStart() { return track.scrollLeft <= 4; }

      function updateArrows() {
        if (!prevBtn || !nextBtn) return;
        if (LOOP) { prevBtn.disabled = false; nextBtn.disabled = false; return; }
        var max = track.scrollWidth - track.clientWidth - 2;
        prevBtn.disabled = track.scrollLeft <= 2;
        nextBtn.disabled = track.scrollLeft >= max;
      }
      function updateProgress() {
        if (!progFill) return;
        var sw = track.scrollWidth, cw = track.clientWidth;
        if (sw <= cw) { progFill.style.width = '100%'; progFill.style.left = '0%'; return; }
        var widthPct = Math.max((cw / sw) * 100, 8);
        progFill.style.width = widthPct + '%';
        progFill.style.left = ((track.scrollLeft / (sw - cw)) * (100 - widthPct)) + '%';
      }

      if (nextBtn) nextBtn.addEventListener('click', function () {
        if (LOOP && atEnd()) { track.scrollTo({ left: 0, behavior: 'smooth' }); }
        else { track.scrollBy({ left: cardStep(), behavior: 'smooth' }); }
      });
      if (prevBtn) prevBtn.addEventListener('click', function () {
        if (LOOP && atStart()) { track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' }); }
        else { track.scrollBy({ left: -cardStep(), behavior: 'smooth' }); }
      });

      track.addEventListener('scroll', function () { updateArrows(); updateProgress(); }, { passive: true });

      /* Mouse drag-to-scroll. Click is suppressed only after a real drag. */
      var down = false, dragging = false, startX = 0, startLeft = 0, moved = false, pid = null;
      var THRESH = 10, FACTOR = 0.7;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; dragging = false; moved = false; startX = e.clientX; startLeft = track.scrollLeft; pid = e.pointerId;
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!dragging) { if (Math.abs(dx) <= THRESH) return; dragging = true; moved = true; track.classList.add('is-dragging'); try { track.setPointerCapture(pid); } catch (err) {} }
        track.scrollLeft = startLeft - dx * FACTOR; e.preventDefault();
      });
      function endDrag() { if (!down) return; down = false; if (dragging) { track.classList.remove('is-dragging'); if (pid !== null) { try { track.releasePointerCapture(pid); } catch (err) {} } } dragging = false; pid = null; }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
      track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);

      if (progTrack) {
        var scrubbing = false;
        var scrub = function (x) {
          var r = progTrack.getBoundingClientRect();
          var p = Math.min(Math.max((x - r.left) / r.width, 0), 1);
          track.scrollLeft = p * (track.scrollWidth - track.clientWidth);
        };
        progTrack.addEventListener('pointerdown', function (e) { scrubbing = true; try { progTrack.setPointerCapture(e.pointerId); } catch (err) {} scrub(e.clientX); });
        progTrack.addEventListener('pointermove', function (e) { if (scrubbing) scrub(e.clientX); });
        progTrack.addEventListener('pointerup', function () { scrubbing = false; });
        progTrack.addEventListener('pointercancel', function () { scrubbing = false; });
      }

      updateArrows(); updateProgress();

      return {
        track: track,
        refresh: function () { updateArrows(); updateProgress(); },
        step: function () {
          if (track.scrollWidth - track.clientWidth < 4) return;
          if (atEnd()) { track.scrollTo({ left: 0, behavior: 'smooth' }); }
          else { track.scrollBy({ left: cardStep(), behavior: 'smooth' }); }
        }
      };
    }

    var panes = {};
    Array.prototype.forEach.call(root.querySelectorAll('.sc__pane'), function (pane) {
      var api = initPane(pane);
      if (api) panes[pane.getAttribute('data-sc-pane')] = api;
    });

    /* ---- Tabs ---- */
    var tabs = root.querySelectorAll('[data-sc-tab]');
    var activeId = null;
    var firstPane = root.querySelector('.sc__pane');
    if (firstPane) activeId = firstPane.getAttribute('data-sc-pane');

    function selectTab(id) {
      if (!id || id === activeId) return;
      activeId = id;
      Array.prototype.forEach.call(root.querySelectorAll('.sc__pane'), function (p) {
        p.hidden = p.getAttribute('data-sc-pane') !== id;
      });
      Array.prototype.forEach.call(tabs, function (t) {
        t.setAttribute('aria-selected', t.getAttribute('data-sc-tab') === id ? 'true' : 'false');
      });
      /* The panel CTA follows the tab. Falls back to the section CTA when the
         tab has none of its own. */
      var ctas = root.querySelectorAll('[data-sc-cta]');
      var matched = false;
      Array.prototype.forEach.call(ctas, function (c) {
        var on = c.getAttribute('data-sc-cta') === id;
        if (on) matched = true;
        c.hidden = !on;
      });
      if (!matched) {
        Array.prototype.forEach.call(ctas, function (c) {
          c.hidden = c.getAttribute('data-sc-cta') !== '__fallback';
        });
      }
      /* A hidden pane has no measurable width, so its arrows and progress bar
         can only be sized once it is on screen. */
      if (panes[id]) panes[id].refresh();
    }

    Array.prototype.forEach.call(tabs, function (t) {
      t.addEventListener('click', function () { selectTab(t.getAttribute('data-sc-tab')); });
      t.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var list = Array.prototype.slice.call(tabs);
        var i = list.indexOf(t);
        var n = e.key === 'ArrowRight' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
        list[n].focus();
        selectTab(list[n].getAttribute('data-sc-tab'));
      });
    });

    /* ---- Auto-play, active pane only ---- */
    var apTimer = null, apPaused = false;
    function apStart() {
      if (!AUTOPLAY || reduce || apPaused || document.hidden) return;
      apStop();
      apTimer = setInterval(function () { if (panes[activeId]) panes[activeId].step(); }, AUTOPLAY_MS);
    }
    function apStop() { if (apTimer) { clearInterval(apTimer); apTimer = null; } }
    if (AUTOPLAY && !reduce) {
      root.addEventListener('mouseenter', function () { apPaused = true; apStop(); });
      root.addEventListener('mouseleave', function () { apPaused = false; apStart(); });
      root.addEventListener('focusin', function () { apPaused = true; apStop(); });
      root.addEventListener('focusout', function () { apPaused = false; apStart(); });
      root.addEventListener('pointerdown', function () { apPaused = true; apStop(); });
      root.addEventListener('pointerup', function () { apPaused = false; apStart(); });
      document.addEventListener('visibilitychange', function () { if (document.hidden) apStop(); else apStart(); });
      apStart();
    }

    window.addEventListener('resize', function () {
      Object.keys(panes).forEach(function (k) { if (k === activeId) panes[k].refresh(); });
    });

    /* Theme editor: a block added or reordered re-renders the section, but
       selecting a block only fires this event - jump to the tab it lives in. */
    document.addEventListener('shopify:block:select', function (e) {
      if (!root.contains(e.target)) return;
      var pane = e.target.closest ? e.target.closest('.sc__pane') : null;
      if (pane) selectTab(pane.getAttribute('data-sc-pane'));
      else if (e.target.hasAttribute && e.target.hasAttribute('data-sc-tab')) selectTab(e.target.getAttribute('data-sc-tab'));
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sc-blog-carousel]'), boot);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
